import type { CollectionField, CollectionFieldSelectList } from '@ikegps/core-api';
import { PoleFieldIds } from './ike.pole.metadata/FieldID';
import type { IkePoleForemanFieldIds } from './ikePoleForeman/FieldID';
import { IPFSpanType, isIPFSpanType } from './ikePoleForeman/Span';

// Recursively get all values from an object
type StringUnion<T> = T extends string
	? T
	: T extends (infer U)[]
		? StringUnion<U>
		: T extends object
			? StringUnion<T[keyof T]>
			: never;

type PLAFieldNames = Extract<StringUnion<typeof PoleFieldIds>, string>;

type IPFFieldNames = `${IkePoleForemanFieldIds}`;
export type PLAToIPFFieldMapping = {
	[K in PLAFieldNames]?: IPFFieldNames;
};
export type IPFToPLAFieldMapping = {
	[K in IPFFieldNames]?: PLAFieldNames;
};

type FieldMigrationFunction = CollectionField extends infer T
	? T extends CollectionField
		? (data: T) => T
		: never
	: never;

export type FieldDataMapping<T> = {
	[K in keyof T]?: FieldMigrationFunction;
};

export const PLAToIPFFieldMap: PLAToIPFFieldMapping = {
	// Top level
	poleID: 'ipf_id',
	images: 'ipf_tagPhotos',
	poleType: 'ipf_type',
	poleOwner: 'ipf_owner',
	agl: 'ipf_tip',
	geographicCoordinate: 'ipf_location',
	note: 'ipf_note',
	trueSizeCapture: 'ipf_ikePhoto',
	status: 'ipf_status',
	plaResult: 'ipf_plaResult',

	// Equipment
	equipment: 'ipf_equipment',
	equipmentType: 'ipf_equipmentType',
	equipmentOrientation: 'ipf_equipmentOrientation',
	equipmentOwner: 'ipf_equipmentOwner',
	equipmentAttachmentHeight: 'ipf_equipmentAttachmentHeight',
	equipmentNote: 'ipf_equipmentNote',

	// Anchor
	anchor: 'ipf_anchor',
	anchorLeadLength: 'ipf_anchorLeadLength',
	anchorOwner: 'ipf_anchorOwner',
	anchorNote: 'ipf_anchorNote',
	// Anchor -> Guys
	guys: 'ipf_anchorGuys',
	guySize: 'ipf_anchorGuySize',
	guyOwner: 'ipf_anchorGuyOwner',
	guyAttachmentHeight: 'ipf_anchorGuyAttachmentHeight',

	// Span
	span: 'ipf_span',
	spanLength: 'ipf_spanLength',
	spanType: 'ipf_spanType',
	spanMidSpanPhoto: 'ipf_spanMidSpanIkePhoto',
	spanNote: 'ipf_spanNote',
	// Span -> Guys
	spanGuys: 'ipf_spanGuy',
	spanGuySize: 'ipf_spanGuySize',
	spanGuyOwner: 'ipf_spanGuyOwner',
	spanGuyAttachmentHeight: 'ipf_spanGuyAttachmentHeight',
} as const;

export const IPFToPLAFieldMap: IPFToPLAFieldMapping = {
	// Top level
	ipf_id: 'poleID',
	ipf_tagPhotos: 'images',
	ipf_type: 'poleType',
	ipf_owner: 'poleOwner',
	ipf_tip: 'agl',
	ipf_location: 'geographicCoordinate',
	ipf_note: 'note',
	ipf_ikePhoto: 'trueSizeCapture',
	ipf_status: 'status',
	ipf_plaResult: 'plaResult',

	// Equipment
	ipf_equipment: 'equipment',
	ipf_equipmentType: 'equipmentType',
	ipf_equipmentOrientation: 'equipmentOrientation',
	ipf_equipmentOwner: 'equipmentOwner',
	ipf_equipmentAttachmentHeight: 'equipmentAttachmentHeight',
	ipf_equipmentNote: 'equipmentNote',

	// Anchor
	ipf_anchor: 'anchor',
	ipf_anchorLeadLength: 'anchorLeadLength',
	ipf_anchorOwner: 'anchorOwner',
	ipf_anchorNote: 'anchorNote',
	// Anchor -> Guys
	ipf_anchorGuys: 'guys',
	ipf_anchorGuySize: 'guySize',
	ipf_anchorGuyOwner: 'guyOwner',
	ipf_anchorGuyAttachmentHeight: 'guyAttachmentHeight',

	// Span
	ipf_span: 'span',
	ipf_spanLength: 'spanLength',
	ipf_spanType: 'spanType',
	ipf_spanMidSpanIkePhoto: 'spanMidSpanPhoto',
	ipf_spanNote: 'spanNote',
	// Span -> Guys
	ipf_spanGuy: 'spanGuys',
	ipf_spanGuySize: 'spanGuySize',
	ipf_spanGuyOwner: 'spanGuyOwner',
	ipf_spanGuyAttachmentHeight: 'spanGuyAttachmentHeight',
} as const;

const legacySpanTypes = ['NEXT_POLE', 'PREVIOUS_POLE', 'OTHER_POLE', 'BUILDING'] as const;

type legacySpanTypes = (typeof legacySpanTypes)[number];

function isLegacySpanType(value: unknown): value is legacySpanTypes {
	return legacySpanTypes.includes(value as legacySpanTypes);
}

export const PLAtoIPFFieldMappingEntries: FieldDataMapping<PLAToIPFFieldMapping> = {
	spanType: (data: CollectionFieldSelectList) => {
		if (data && typeof data === 'string' && isLegacySpanType(data)) {
			switch (data) {
				case 'NEXT_POLE':
					return IPFSpanType.FORE_SPAN;
				case 'PREVIOUS_POLE':
					return IPFSpanType.BACK_SPAN;
				case 'OTHER_POLE':
				case 'BUILDING':
					return IPFSpanType.OTHER;
				default: {
					const x: never = data;
					return x;
				}
			}
		}
		return data;
	},
} as const;

export const IPFToPLAFieldMappingEntries: FieldDataMapping<IPFToPLAFieldMapping> = {
	ipf_spanType: (data: CollectionFieldSelectList) => {
		if (data && typeof data === 'string' && isIPFSpanType(data)) {
			switch (data) {
				case IPFSpanType.FORE_SPAN:
					return 'NEXT_POLE';
				case IPFSpanType.BACK_SPAN:
					return 'PREVIOUS_POLE';
				case IPFSpanType.OTHER:
					return 'OTHER_POLE';
				default: {
					const x: never = data;
					return x;
				}
			}
		}
		return data;
	},
} as const;
